import { getAnalytics, isSupported } from 'firebase/analytics';
import type { Analytics } from 'firebase/analytics';
import { app } from '../../config/firebase';

let analyticsInstance: Analytics | null = null;

/**
 * Initialise Firebase Analytics.
 *
 * In non-production environments, enables DebugView by calling
 * gtag('config', measurementId, { debug_mode: true }) after initialisation.
 * Firebase creates window.gtag synchronously inside getAnalytics(), so the
 * command is enqueued in window.dataLayer before gtag.js loads — gtag.js then
 * processes it and adds _dbg=1 to every collect request, which is the signal
 * Firebase DebugView uses to show events in real time.
 *
 * Returns the analytics singleton. Fail-silent on any error.
 */
export function initAnalytics(): Analytics {
  if (analyticsInstance) return analyticsInstance;

  const measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID;
  if (!measurementId) {
    console.error(
      '[Analytics] VITE_FIREBASE_MEASUREMENT_ID is not set — Analytics disabled. Add it to .env and restart the dev server.',
    );
    analyticsInstance = {} as Analytics;
    return analyticsInstance;
  }

  try {
    analyticsInstance = getAnalytics(app);

    if (!import.meta.env.PROD) {
      type GtagFn = (cmd: string, id: string, params: Record<string, unknown>) => void;
      const gtagFn = (window as unknown as { gtag?: GtagFn }).gtag;
      if (gtagFn) {
        gtagFn('config', measurementId, { debug_mode: true });
      }

      console.log('[Analytics] Initialised ✓ — measurementId:', measurementId, '| DebugView: ON');

      isSupported().then((supported) => {
        if (!supported) {
          console.warn(
            '[Analytics] isSupported() = false — events may not be sent (cookies blocked?)',
          );
        } else {
          console.log('[Analytics] isSupported() = true ✓');
        }
      });
    }
  } catch (e) {
    console.error('[Analytics] Initialisation failed:', e);
    analyticsInstance = {} as Analytics;
  }

  return analyticsInstance;
}
